import { useEffect } from 'react';

import { captureMessage } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { HUBSPOT_COOKIE_KEY } from '@cast/constants';

import { parseApiError } from '@cast/api-client';
import { gtmPushEvent } from '@cast/infrastructure-analytics';
import { apiClient } from '@cast/infrastructure-api';
import { useUser } from '@cast/infrastructure-auth';
import { HubspotContact, HubspotObject } from '@cast/types';
import { getCookie } from '@cast/utils';

import { useHubspotContact } from 'core/hubspot';

type Props = {
  pageName: string;
  enabled: boolean;
};

export const useCreateFullHubspotContactMutation = ({
  pageName,
  enabled,
}: Props) => {
  const user = useUser();
  const { contactState, setContactState } = useHubspotContact();

  const { mutate } = useMutation({
    mutationFn: (contact: HubspotContact) => {
      const hubspotCookie = getCookie(HUBSPOT_COOKIE_KEY);

      return apiClient.hubspot.createFullHubspotContact({
        submittedAt: new Date().getTime(),
        fields: [
          {
            objectTypeId: HubspotObject.CONTACT,
            name: 'email',
            value: user.email,
          },
          {
            objectTypeId: HubspotObject.CONTACT,
            name: 'firstname',
            value: contact.firstName,
          },
          {
            objectTypeId: HubspotObject.CONTACT,
            name: 'lastname',
            value: contact.lastName,
          },
          {
            objectTypeId: HubspotObject.CONTACT,
            name: 'company',
            value: contact.company,
          },
          {
            objectTypeId: HubspotObject.CONTACT,
            name: 'phone',
            value: contact.phone,
          },
          {
            objectTypeId: HubspotObject.CONTACT,
            name: 'jobtitle',
            value: contact.jobTitle,
          },
        ],
        context: {
          hutk: hubspotCookie!,
          pageUri: window.document.URL,
          pageName,
        },
      });
    },
    onSuccess: () => {
      setContactState('success');
      const { email, name, id } = user;
      gtmPushEvent(
        'signup_completed',
        {
          'user-id': id,
          email,
          name,
        },
        email
      );
    },
    onError: (error, payload) => {
      setContactState(payload);
      const parsedError = parseApiError(error);

      captureMessage(
        `Hubspot: Unable to submit FULL signup form. Payload: ${JSON.stringify(
          payload
        )}; Error: ${parsedError}`
      );
    },
  });

  useEffect(() => {
    if (enabled && typeof contactState !== 'string') {
      mutate(contactState);
    }

    // excluding contactFallback since we only need to fire this event once per login
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, mutate]);

  return { mutate };
};
